import { Button, Checkbox, Group, NumberInput, Paper, Stack, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { formatHours } from '../../lib/format';
import { brand } from '../../theme/brand';

export interface PatternDay {
  weekday: number;
  startTime: string;
  endTime: string;
  unpaidBreakMinutes: number;
}

/** Sunday = 0, matching the business-day and pricing-schedule maths on the server. */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "HH:MM" → minutes past midnight, or null when the field is empty or half-typed. */
function minutesOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Minutes a shift covers, allowing for one that ends after midnight.
 *
 * A bar closing at 01:00 is Tuesday, not an edge case, and naive subtraction would report it as
 * minus nineteen hours. Break time is removed here because the house rule is that breaks are
 * unpaid, so the scheduled figure a manager compares against must already exclude them.
 */
export function scheduledMinutes(day: PatternDay): number {
  const start = minutesOfDay(day.startTime);
  const end = minutesOfDay(day.endTime);
  if (start === null || end === null) return 0;
  const span = end > start ? end - start : end + 24 * 60 - start;
  return Math.max(0, span - (day.unpaidBreakMinutes || 0));
}

/**
 * A week as a person actually works it.
 *
 * Deliberately a seven-row grid rather than a list you add rows to: a schedule is answering "which
 * days are you on, and when?", and a day off is a real answer, not a missing record. The two shapes
 * this bar runs — six shorter days, or five days with two long ones and two days off — are both
 * one glance to read and one click to change.
 */
export function WeeklyPatternEditor({
  value,
  onChange,
  disabled,
}: {
  value: PatternDay[];
  onChange: (next: PatternDay[]) => void;
  disabled?: boolean;
}) {
  const byWeekday = new Map(value.map((d) => [d.weekday, d]));
  const weekMinutes = value.reduce((sum, d) => sum + scheduledMinutes(d), 0);

  const setDay = (weekday: number, next: PatternDay | null) => {
    const rest = value.filter((d) => d.weekday !== weekday);
    onChange(next ? [...rest, next].sort((a, b) => a.weekday - b.weekday) : rest);
  };

  const firstWorking = value[0];

  return (
    <Stack gap="xs">
      {DAY_NAMES.map((name, weekday) => {
        const day = byWeekday.get(weekday);
        const working = Boolean(day);
        return (
          <Paper key={name} withBorder p="xs" radius="sm" bg={working ? undefined : '#00000008'}>
            <Group gap="md" wrap="nowrap" align="center">
              <Checkbox
                label={name}
                checked={working}
                disabled={disabled}
                onChange={(e) =>
                  setDay(
                    weekday,
                    e.currentTarget.checked
                      ? {
                          weekday,
                          // Seed a new day from one already set, so filling a week is a few clicks
                          // rather than typing the same two times five times over.
                          startTime: firstWorking?.startTime ?? '10:00',
                          endTime: firstWorking?.endTime ?? '17:00',
                          unpaidBreakMinutes: firstWorking?.unpaidBreakMinutes ?? 0,
                        }
                      : null,
                  )
                }
                styles={{ label: { fontWeight: 600, width: 96 } }}
              />

              {day ? (
                <>
                  <TimeInput
                    aria-label={`${name} start`}
                    value={day.startTime}
                    disabled={disabled}
                    onChange={(e) => setDay(weekday, { ...day, startTime: e.currentTarget.value })}
                    style={{ width: 116 }}
                  />
                  <Text size="sm" c="dimmed">
                    to
                  </Text>
                  <TimeInput
                    aria-label={`${name} end`}
                    value={day.endTime}
                    disabled={disabled}
                    onChange={(e) => setDay(weekday, { ...day, endTime: e.currentTarget.value })}
                    style={{ width: 116 }}
                  />
                  <NumberInput
                    aria-label={`${name} unpaid break minutes`}
                    value={day.unpaidBreakMinutes}
                    disabled={disabled}
                    onChange={(v) => setDay(weekday, { ...day, unpaidBreakMinutes: Number(v) || 0 })}
                    min={0}
                    step={15}
                    suffix=" min break"
                    style={{ width: 150 }}
                  />
                  <Text size="sm" fw={600} ml="auto" style={{ color: brand.sea, whiteSpace: 'nowrap' }}>
                    {formatHours(scheduledMinutes(day))}
                  </Text>
                </>
              ) : (
                <Text size="sm" c="dimmed" fs="italic">
                  Day off
                </Text>
              )}
            </Group>
          </Paper>
        );
      })}

      <Group justify="space-between" mt="xs">
        <Group gap="xs">
          {firstWorking && (
            <Button
              size="compact-sm"
              variant="subtle"
              disabled={disabled}
              onClick={() =>
                onChange(
                  value.map((d) => ({
                    ...d,
                    startTime: firstWorking.startTime,
                    endTime: firstWorking.endTime,
                    unpaidBreakMinutes: firstWorking.unpaidBreakMinutes,
                  })),
                )
              }
            >
              Use these times on every working day
            </Button>
          )}
        </Group>
        <Text size="sm" fw={700}>
          {value.length} {value.length === 1 ? 'day' : 'days'} a week · {formatHours(weekMinutes)} total
        </Text>
      </Group>
    </Stack>
  );
}
